import agwaAddressStyles from './agwa-address.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import type { Address } from '../../api/agwa-api-model';
import { COUNTRIES, findCountryByCode, findCountryByName } from './countries';
import type SlSelect from '@shoelace-style/shoelace/dist/components/select/select.js';

import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/skeleton/skeleton.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import { addressLines } from './address-format';
import { terms } from '../../language/terms';

/**
 * A reusable address component for displaying and editing addresses.
 *
 * In read-only mode it can also show a stored static map image (pass `map-image-url` / `.mapImageUrl`
 * - typically the server-generated site map served via the document proxy). Map-based location
 * capture in edit mode lives in the separate `<agwa-site-address-edit>` component, which reuses this
 * one for its form fields.
 *
 * @element agwa-address
 * @fires agwa-address-change - Fired when address fields change in edit mode
 */
@customElement('agwa-address')
export class AgwaAddress extends LitElement {
  static styles = [sharedStyles, unsafeCSS(agwaAddressStyles)];

  /** The address data to display or edit */
  @property({ type: Object }) address: Address | null = null;

  /** Whether the address is editable */
  @property({ type: Boolean }) editable = false;

  /**
   * Optional URL of a stored static map image to show below the address in read-only mode
   * (e.g. the server-generated site map served through the document proxy). No effect in edit mode.
   */
  @property({ type: String, attribute: 'map-image-url' }) mapImageUrl:
    string | null = null;

  /** Whether to show a location icon next to the address */
  @property({ type: Boolean, attribute: 'show-icon' }) showIcon = false;

  /** Whether the address is loading */
  @property({ type: Boolean }) loading = false;

  /**
   * When true, marks the address fields as required (Unit stays optional).
   * Opt-in so the component remains portable across hosts that do not need
   * a fully mandatory address.
   */
  @property({ type: Boolean, attribute: 'require-fields' }) requireFields =
    false;

  /** Label to show when address is empty */
  @property({ type: String, attribute: 'empty-label' }) emptyLabel =
    'No address';

  private handleFieldChange(field: keyof Address, value: string) {
    const updated = { ...this.address, [field]: value || undefined } as Address;
    this.dispatchEvent(
      new CustomEvent('agwa-address-change', {
        detail: { address: updated, field, value },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Country selection sets both the full country name and the ISO 3166-1
   * alpha-2 code so downstream spatial features can rely on the code.
   */
  private handleCountryChange(code: string) {
    const country = findCountryByCode(code);
    const updated = {
      ...this.address,
      country: country?.name,
      countryCode: country?.code,
    } as Address;
    this.dispatchEvent(
      new CustomEvent('agwa-address-change', {
        detail: { address: updated, field: 'country', value: country?.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  private get hasAddress(): boolean {
    if (!this.address) return false;
    return Boolean(
      this.address.streetName ||
      this.address.suburb ||
      this.address.state ||
      this.address.placeName ||
      this.address.formattedAddress
    );
  }

  private renderLoading() {
    return html`
      <div class="address-loading">
        <sl-skeleton
          effect="pulse"
          style="width: 80%; height: 1rem;"
        ></sl-skeleton>
        <sl-skeleton
          effect="pulse"
          style="width: 60%; height: 1rem;"
        ></sl-skeleton>
      </div>
    `;
  }

  private renderReadonly() {
    const hasImage = !!this.mapImageUrl;
    if (!this.hasAddress && !hasImage) {
      return html`<span class="address-empty">${this.emptyLabel}</span>`;
    }

    const text = this.hasAddress ? this.renderReadonlyText() : nothing;

    return html`
      <div class="address-readonly-wrap">
        ${this.hasAddress && this.showIcon ? this.wrapWithIcon(text) : text}
        ${
          hasImage
            ? html`<img
                class="address-map-image"
                src=${this.mapImageUrl!}
                alt="Map of the ${terms.loginSite.lower} address"
                loading="lazy"
              />`
            : nothing
        }
      </div>
    `;
  }

  /**
   * Renders through the shared address formatter, so this panel, the editor and the server's
   * Address.ToDisplayString all produce the same string for the same site. The place name leads
   * when it adds information and is dropped when it merely repeats the street line.
   */
  private renderReadonlyText() {
    const lines = addressLines(this.address);
    if (lines.length === 0) return nothing;

    const country = this.address?.country?.trim();
    return html`
      <div class="address-readonly">
        ${lines.map(
          (line) =>
            html`<span
              class="address-line ${line === country ? 'secondary' : ''}"
              >${line}</span
            >`
        )}
      </div>
    `;
  }

  private wrapWithIcon(content: unknown) {
    return html`
      <div class="address-with-icon">
        <sl-icon name="geo-alt" class="address-icon"></sl-icon>
        ${content}
      </div>
    `;
  }

  private renderEditable() {
    const addr = this.address || ({} as Address);

    return html`
      <div class="address-edit">
        <div class="address-row">
          <sl-input
            class="narrow"
            label="Unit"
            placeholder="e.g. 5"
            .value=${addr.unitNumber || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'unitNumber',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
          <sl-input
            class="narrow"
            label="Street Number"
            placeholder="e.g. 42"
            ?required=${this.requireFields}
            .value=${addr.streetNumber || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'streetNumber',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
          <sl-input
            label="Street Name"
            placeholder="e.g. Smith Street"
            ?required=${this.requireFields}
            .value=${addr.streetName || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'streetName',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
        </div>

        <div class="address-row">
          <sl-input
            label="Suburb/City"
            placeholder="e.g. Melbourne"
            ?required=${this.requireFields}
            .value=${addr.suburb || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'suburb',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
          <sl-input
            class="medium"
            label="State"
            placeholder="e.g. VIC"
            ?required=${this.requireFields}
            .value=${addr.state || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'state',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
          <sl-input
            class="narrow"
            label="Postcode"
            placeholder="e.g. 3000"
            ?required=${this.requireFields}
            .value=${addr.postcode || ''}
            @sl-input=${(e: CustomEvent) =>
              this.handleFieldChange(
                'postcode',
                (e.target as HTMLInputElement).value
              )}
          ></sl-input>
        </div>

        <div class="address-row">
          <sl-select
            label="Country"
            placeholder="Select country"
            clearable
            ?required=${this.requireFields}
            .value=${
              addr.countryCode?.toUpperCase() ||
              findCountryByName(addr.country)?.code ||
              ''
            }
            @sl-change=${(e: CustomEvent) =>
              this.handleCountryChange(
                ((e.target as SlSelect).value as string) || ''
              )}
          >
            ${COUNTRIES.map(
              (c) => html`<sl-option value=${c.code}>${c.name}</sl-option>`
            )}
          </sl-select>
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return this.renderLoading();
    }

    return this.editable ? this.renderEditable() : this.renderReadonly();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-address': AgwaAddress;
  }
}
